import { useState, useEffect, useCallback } from 'react';
import { Search, Globe, ExternalLink, Loader2, Package } from 'lucide-react';
import { useList } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface OrderStatusConfig {
  id: number;
  name: string;
  color: string | null;
}

interface OrderUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface OrderItemTarget {
  name: string;
}

interface OrderItem {
  id: number;
  targetType: string;
  target: OrderItemTarget | null;
}

interface Order {
  id: number;
  total: string;
  paidAmount: string;
  currency: string;
  createdAt: string;
  statusConfig: OrderStatusConfig;
  user: OrderUser;
  items: OrderItem[];
}

interface PublishWithOrderModalProps {
  open: boolean;
  onClose: () => void;
  messageIds: number[];
  chatTitle: string;
  currentOrderId?: number | null;
  onPublish: (messageIds: number[], orderId?: number) => Promise<void>;
}

export function PublishWithOrderModal({
  open,
  onClose,
  messageIds,
  currentOrderId,
  onPublish,
}: PublishWithOrderModalProps) {
  const messageCount = messageIds.length;
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<number | undefined>(
    currentOrderId ?? undefined
  );
  const [isPublishing, setIsPublishing] = useState(false);

  // Fetch orders with search
  const ordersQuery = useList<Order>({
    resource: 'orders',
    pagination: { pageSize: 20 },
    filters: search ? [{ field: 'q', operator: 'contains', value: search }] : [],
    sorters: [{ field: 'createdAt', order: 'desc' }],
    queryOptions: {
      enabled: open,
    },
  });

  const isLoading = ordersQuery.query?.isLoading ?? false;
  const orders: Order[] = ordersQuery.result?.data ?? [];

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedOrderId(currentOrderId ?? undefined);
    }
  }, [open, currentOrderId]);

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      await onPublish(messageIds, selectedOrderId);
      onClose();
    } finally {
      setIsPublishing(false);
    }
  }, [messageIds, selectedOrderId, onPublish, onClose]);

  const handlePublishWithoutOrder = useCallback(async () => {
    setIsPublishing(true);
    try {
      await onPublish(messageIds);
      onClose();
    } finally {
      setIsPublishing(false);
    }
  }, [messageIds, onPublish, onClose]);

  const formatCurrency = (amount: string, currency: string) => {
    const num = parseFloat(amount);
    if (!Number.isFinite(num)) {
      return `${amount} ${currency}`;
    }
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const getPaymentLabel = (order: Order) => {
    const paid = parseFloat(order.paidAmount);
    const total = parseFloat(order.total);
    if (paid >= total && total > 0) {
      return { text: 'Оплачено', className: 'text-green-600' };
    }
    if (paid > 0) {
      return { text: 'Частично', className: 'text-yellow-600' };
    }
    return { text: 'Не оплачено', className: 'text-red-500' };
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Опубликовать сообщения
          </DialogTitle>
          <DialogDescription>
            {messageCount} {messageCount === 1 ? 'сообщение' : (messageCount < 5 ? 'сообщения' : 'сообщений')} будут помечены как публичные.
            Выберите заказ для привязки диалога (опционально).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current linked order info */}
          {currentOrderId && (
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-primary/5 border-primary/30">
              <Package className="h-4 w-4 text-primary" />
              <span className="text-sm">
                Чат привязан к заказу <strong>#{currentOrderId}</strong>
              </span>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по номеру заказа или пользователю..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Orders list */}
          <ScrollArea className="h-[250px] -mx-2 px-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <p className="text-sm">
                  {search ? 'Заказы не найдены' : 'Нет заказов'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => {
                  const isSelected = order.id === selectedOrderId;
                  const userName = [order.user.firstName, order.user.lastName]
                    .filter(Boolean)
                    .join(' ');
                  const paymentInfo = getPaymentLabel(order);
                  const firstItemName = order.items[0]?.target?.name;

                  return (
                    <div
                      key={order.id}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer',
                        isSelected
                          ? 'bg-primary/10 border-primary/50'
                          : 'hover:bg-accent'
                      )}
                      onClick={() => setSelectedOrderId(isSelected ? undefined : order.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-sm">#{order.id}</span>
                          {order.statusConfig && (
                            <Badge
                              variant="outline"
                              className="text-[11px] px-1.5 py-0"
                              style={{
                                borderColor: order.statusConfig.color ?? undefined,
                                color: order.statusConfig.color ?? undefined,
                              }}
                            >
                              {order.statusConfig.name}
                            </Badge>
                          )}
                          <span className={cn('text-[11px] font-medium', paymentInfo.className)}>
                            {paymentInfo.text}
                          </span>
                          {isSelected && (
                            <Badge className="text-[11px] bg-primary px-1.5 py-0">
                              Выбран
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                          <span>{userName || `Пользователь #${order.user.id}`}</span>
                          {order.user.username && (
                            <span className="text-primary/70">@{order.user.username}</span>
                          )}
                          <span>·</span>
                          <span>{formatDate(order.createdAt)}</span>
                        </div>
                        {firstItemName && (
                          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                            {firstItemName}
                            {order.items.length > 1 && ` +${order.items.length - 1}`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        <span className="font-mono font-medium text-sm">
                          {formatCurrency(order.total, order.currency)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          asChild
                        >
                          <a
                            href={`/orders/show/${order.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </ScrollArea>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handlePublishWithoutOrder}
            disabled={isPublishing}
            className="w-full sm:w-auto"
          >
            {isPublishing && !selectedOrderId ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Globe className="h-4 w-4 mr-2" />
            )}
            Без привязки
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isPublishing || !selectedOrderId}
            className="w-full sm:w-auto"
          >
            {isPublishing && selectedOrderId ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Package className="h-4 w-4 mr-2" />
            )}
            Привязать к #{selectedOrderId || '...'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
